import { getExtensionValue, getReferenceString, WithId } from "@medplum/core";
import { BundleEntry, PlanDefinition, Task } from "@medplum/fhirtypes";

import { ChecklistTaskChangeDirection, ChecklistVisibility } from "@/constants/checklist";
import { Extensions } from "@/fhir/enums/extensions";
import { ChecklistCodes, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { ChecklistTask } from "@/types/checklist-task";

import { joinReferenceString, setExtensionValue } from "./medplum";

// Private checklist items need owner=%profile to satisfy the AccessPolicy rule that grants read access to them.
const getChecklistVisibilityOwner = (
  visibility?: string,
  practitionerId?: string,
): Pick<Task, "owner"> => {
  return visibility === ChecklistVisibility.Private && practitionerId
    ? { owner: { reference: joinReferenceString("Practitioner", practitionerId) } }
    : {};
};

export const getChecklistByTemplateResource = (
  template: PlanDefinition,
  clientId: string,
  fullUrl: string,
  visibility?: string,
  practitionerId?: string,
): BundleEntry => {
  const checklist: Task = {
    resourceType: "Task",
    status: "in-progress",
    intent: "plan",
    for: { reference: joinReferenceString("Patient", clientId) },
    ...getChecklistVisibilityOwner(visibility, practitionerId),
    code: {
      coding: [
        {
          code: template.title?.trim(),
          display: template.title?.trim(),
          system: Systems.QH,
        },
        {
          code: ChecklistCodes.Checklist,
          display: ChecklistCodes.Checklist,
          system: Systems.QH,
        },
        ...(visibility
          ? [
              {
                code: visibility,
                display: visibility,
                system: QuiltedHealthCodeSystems.ChecklistVisibility,
              },
            ]
          : []),
      ],
    },
  };

  return {
    fullUrl,
    resource: checklist,
    request: {
      method: "POST",
      url: "Task",
    },
  };
};

export const getChecklistTaskByTemplateResource = (
  title: string,
  clientId: string,
  fullUrl: string,
  position: number,
  visibility?: string,
  practitionerId?: string,
): BundleEntry => {
  const task: Task = {
    resourceType: "Task",
    status: "in-progress",
    intent: "plan",
    for: { reference: joinReferenceString("Patient", clientId) },
    partOf: [{ reference: fullUrl }],
    ...getChecklistVisibilityOwner(visibility, practitionerId),
    code: {
      coding: [
        {
          code: title?.trim(),
          display: title?.trim(),
          system: Systems.QH,
        },
        {
          code: ChecklistCodes.ChecklistTask,
          display: ChecklistCodes.ChecklistTask,
          system: Systems.QH,
        },
        ...(visibility
          ? [
              {
                code: visibility,
                display: visibility,
                system: QuiltedHealthCodeSystems.ChecklistVisibility,
              },
            ]
          : []),
      ],
    },
    extension: [
      {
        url: Extensions.ChecklistTaskOrder,
        valueInteger: position,
      },
    ],
  };

  return {
    resource: task,
    request: {
      method: "POST",
      url: "Task",
    },
  };
};

export const parseTasksResultsToChecklistTask = (tasksResults: Task[]): ChecklistTask[] => {
  const checklists = tasksResults.filter((task) => !task?.partOf);
  const tasksFiltered = tasksResults.filter((task) => task?.partOf);

  const checklistTasks = checklists.map((checklist) => {
    const tasksArray = tasksFiltered.filter(
      (task) => task.partOf?.[0]?.reference === getReferenceString(checklist),
    );

    const tasks = tasksArray.reduce(
      (acc, task) => {
        acc[task.id!] = task;
        return acc;
      },
      {} as Record<string, Task>,
    );

    return {
      ...checklist,
      tasks,
    };
  });

  return checklistTasks;
};

export const updateChecklistTasksOrder = (
  tasks: Task[],
  changeDirection: ChecklistTaskChangeDirection,
): Task[] => {
  return tasks.map((task) => {
    const position = getExtensionValue(task, Extensions.ChecklistTaskOrder) as number;

    return {
      ...task,
      extension: setExtensionValue(task.extension, Extensions.ChecklistTaskOrder, {
        valueInteger: position + (changeDirection === ChecklistTaskChangeDirection.Up ? 1 : -1),
      }),
    };
  });
};

export const sortTasksByOrder = (tasks: WithId<Task>[]): WithId<Task>[] => {
  if (!tasks?.length) return [];

  return tasks.sort((a, b) => {
    const aOrder =
      a.extension?.find((extension) => extension.url === Extensions.ChecklistTaskOrder)
        ?.valueInteger ?? 0;
    const bOrder =
      b.extension?.find((extension) => extension.url === Extensions.ChecklistTaskOrder)
        ?.valueInteger ?? 0;
    return aOrder - bOrder;
  });
};
